import { FlowRangeAggregation } from 'src/app/services/flow-dashboard/flow-dashboard.models';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';

/**
 * Metric settings owned by Deco Flow.
 *
 * This dashboard intentionally uses the existing Flow setting IDs, so it can be introduced without
 * changing Resource Flow or the shared Flow presentation.
 */
export const DECO_FLOW_VALUE_MODES = [
    'total',
    'average',
    'percentile',
    'flow records',
    'players',
] as const;

export type DecoFlowStatistic = (typeof DECO_FLOW_VALUE_MODES)[number];

export const DECO_FLOW_VALUE_MODE_OPTIONS = [
    { value: 'total', label: 'TOTAL' },
    { value: 'average', label: 'AVERAGE' },
    { value: 'percentile', label: 'MEDIAN / PERCENTILE' },
    { value: 'flow records', label: 'FLOW RECORDS' },
    { value: 'players', label: 'PLAYERS' },
] as const;

/** `deu` (daily engaged users) are the players with a positive flow on the given side. */
export const DECO_FLOW_POPULATIONS = ['deu', 'dau'] as const;

export type DecoFlowPopulation = (typeof DECO_FLOW_POPULATIONS)[number];

export const DECO_FLOW_PERCENTILES = [50, 75, 90, 99] as const;

export type DecoFlowPercentile = (typeof DECO_FLOW_PERCENTILES)[number];

export const DECO_FLOW_PERCENTILE_OPTIONS = [
    { value: 50, label: 'P50' },
    { value: 75, label: 'P75' },
    { value: 90, label: 'P90' },
    { value: 99, label: 'P99' },
] as const;

export const DECO_FLOW_PERCENTILE_SETTING_IDS: Record<
    DecoFlowPercentile,
    QueryContextSettingID
> = {
    50: QueryContextSettingID.SHOW_PERCENTILE_50,
    75: QueryContextSettingID.SHOW_PERCENTILE_75,
    90: QueryContextSettingID.SHOW_PERCENTILE_90,
    99: QueryContextSettingID.SHOW_PERCENTILE_99,
};

export interface DecoFlowMetric {
    statistic: DecoFlowStatistic;
    population: DecoFlowPopulation;
    percentiles: DecoFlowPercentile[];
    primaryPercentile: DecoFlowPercentile;
    rangeAggregation: FlowRangeAggregation;
    unit: 'decorations' | 'flow records' | 'players';
    label: string;
}

export function getDecoFlowStatistic(
    context: IQueryContext
): DecoFlowStatistic {
    return context.settings[QueryContextSettingID.FLOW_VALUE_MODE] ?? 'total';
}

export function getDecoFlowPercentiles(
    context: IQueryContext
): DecoFlowPercentile[] {
    const selected = DECO_FLOW_PERCENTILES.filter(
        (percentile) =>
            context.settings[DECO_FLOW_PERCENTILE_SETTING_IDS[percentile]]
    );
    return selected;
}

function getDecoFlowPopulation(context: IQueryContext): DecoFlowPopulation {
    return context.settings[QueryContextSettingID.FLOW_POPULATION] ?? 'deu';
}

export function decoFlowPercentileLabel(
    percentile: DecoFlowPercentile
): string {
    return `P${percentile}`;
}

export function formatDecoFlowValue(value: number): string {
    return Math.abs(value) >= 1000
        ? EChartsAxisHelper.axisNumberLabelFormatter(value)
        : parseFloat(value.toFixed(2)).toString();
}

export function getDecoFlowMetric(context: IQueryContext): DecoFlowMetric {
    const statistic = getDecoFlowStatistic(context);
    const population = getDecoFlowPopulation(context);
    const percentiles = getDecoFlowPercentiles(context);
    const unit =
        statistic === 'flow records'
            ? 'flow records'
            : statistic === 'players'
              ? 'players'
              : 'decorations';
    const label =
        statistic === 'percentile'
            ? `${percentiles
                  .map(decoFlowPercentileLabel)
                  .join(' + ')} per ${population}`
            : statistic === 'average'
              ? `average per ${population}`
              : statistic;
    return {
        statistic,
        population,
        percentiles,
        primaryPercentile: percentiles[0],
        rangeAggregation:
            statistic === 'total' ||
            statistic === 'flow records' ||
            statistic === 'players'
                ? 'sum'
                : 'mean',
        unit,
        label,
    };
}
